/**
 * LoginGlobePanel — full-height left panel with a rotating photoreal Earth.
 *
 * Visual goals (matching the image-2 mock):
 *   - NASA Blue Marble texture on the day side, city-lights emissive on night.
 *   - Subtle atmospheric rim glow.
 *   - Floating white "price chip" cards with map-pin icon, drop shadow, and a
 *     glowing blue dot anchoring them to the surface.
 *   - Starfield space backdrop with a faint nebula wash.
 *   - "Email, Google, and Facebook access" pill top-left.
 *   - Bottom: "Step into the live rental globe" headline with "globe" tinted
 *     RoomHora blue + a wide "Globe view is alive" status card with sub-copy
 *     "Prices, pins, and owners in motion".
 *
 * Loads textures from unpkg's CORS-friendly three-globe example assets so we
 * don't need to bundle multi-MB images. If those fetch fails (offline / CDN
 * outage) we fall back to a tasteful procedural shaded sphere so the page
 * never renders empty space.
 */
import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

// ─── City pins (lat/lng of real listings shown in the demo) ──────────────────
const PINS = [
  { lat:  48.8566, lng:   2.3522, price: '€920',    city: 'Paris, France'      },
  { lat:  27.7172, lng:  85.3240, price: 'NPR 28k',  city: 'Kathmandu, Nepal'  },
  { lat:  25.2048, lng:  55.2708, price: 'AED 3.2k', city: 'Dubai, UAE'        },
  { lat: -33.8688, lng: 151.2093, price: '$2.4k',    city: 'Sydney, Australia' },
  { lat:  40.7128, lng: -74.0060, price: '$3.1k',    city: 'New York, USA'     },
];

const GLOBE_RADIUS = 2.1;
const SPIN_SPEED   = 0.0011;

// CORS-friendly, well-maintained NASA texture mirrors via unpkg.
const TEX = {
  day:    'https://unpkg.com/three-globe@2.31.1/example/img/earth-blue-marble.jpg',
  night:  'https://unpkg.com/three-globe@2.31.1/example/img/earth-night.jpg',
  bump:   'https://unpkg.com/three-globe@2.31.1/example/img/earth-topology.png',
  stars:  'https://unpkg.com/three-globe@2.31.1/example/img/night-sky.png',
};

function latLngToVec3(lat, lng, r) {
  const phi   = (90 - lat)  * (Math.PI / 180);
  const theta = (lng + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -r * Math.sin(phi) * Math.cos(theta),
     r * Math.cos(phi),
     r * Math.sin(phi) * Math.sin(theta),
  );
}

export default function LoginGlobePanel() {
  const wrapRef    = useRef(null);
  const rendRef    = useRef(null);
  const camRef     = useRef(null);
  const globeRef   = useRef(null);
  const sceneRef   = useRef(null);
  const localPins  = useRef([]);

  const [overlays, setOverlays] = useState(
    PINS.map(() => ({ x: 0, y: 0, visible: false, depth: 0 })),
  );

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    let animId;
    let canvas;
    let ro;

    const boot = requestAnimationFrame(() => {
      const W = wrap.offsetWidth  || window.innerWidth  * 0.53;
      const H = wrap.offsetHeight || window.innerHeight;

      // ── Renderer ───────────────────────────────────────────────────────────
      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(W, H);
      renderer.setClearColor(0x000000, 0);
      canvas = renderer.domElement;
      canvas.style.position = 'absolute';
      canvas.style.inset    = '0';
      canvas.style.width    = '100%';
      canvas.style.height   = '100%';
      wrap.appendChild(canvas);
      rendRef.current = renderer;

      // ── Scene ──────────────────────────────────────────────────────────────
      const scene = new THREE.Scene();
      sceneRef.current = scene;

      // ── Camera ─────────────────────────────────────────────────────────────
      const camera = new THREE.PerspectiveCamera(40, W / H, 0.1, 1000);
      camera.position.set(0, 0.2, 6.4);
      camera.lookAt(0, 0, 0);
      camRef.current = camera;

      // ── Globe group ────────────────────────────────────────────────────────
      const globe = new THREE.Group();
      // Tilt slightly so the texture reads as Earth viewed from a satellite.
      globe.rotation.x = -0.18;
      scene.add(globe);
      globeRef.current = globe;

      const geoSphere = new THREE.SphereGeometry(GLOBE_RADIUS, 96, 96);

      // Texture loader with crossOrigin set so unpkg images load cleanly.
      const loader = new THREE.TextureLoader();
      loader.setCrossOrigin('anonymous');

      // 1. Day-side Earth (Blue Marble).
      const dayMat = new THREE.MeshPhongMaterial({
        color:        0xffffff,
        specular:     0x223344,
        shininess:    24,
      });
      const dayMesh = new THREE.Mesh(geoSphere, dayMat);
      globe.add(dayMesh);

      // 2. Night-side city lights — a second sphere blended additively.
      const nightMat = new THREE.MeshBasicMaterial({
        color:        0xffffff,
        transparent:  true,
        opacity:      0.0, // raised after texture loads
        blending:     THREE.AdditiveBlending,
        depthWrite:   false,
      });
      const nightMesh = new THREE.Mesh(geoSphere, nightMat);
      globe.add(nightMesh);

      // Async load — gracefully degrade if a texture 404s.
      loader.load(
        TEX.day,
        (tex) => {
          tex.colorSpace = THREE.SRGBColorSpace;
          dayMat.map = tex;
          dayMat.needsUpdate = true;
        },
        undefined,
        () => {
          // Fallback: keep the sphere as a flat ocean-blue if texture fails.
          dayMat.color.set(0x1f4d80);
          dayMat.needsUpdate = true;
        },
      );
      loader.load(TEX.bump, (tex) => {
        dayMat.bumpMap = tex;
        dayMat.bumpScale = 0.04;
        dayMat.needsUpdate = true;
      });
      loader.load(TEX.night, (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace;
        nightMat.map = tex;
        nightMat.opacity = 0.85;
        nightMat.needsUpdate = true;
      });

      // 3. Atmospheric rim glow (back-side blue halo).
      const atmoMat = new THREE.ShaderMaterial({
        transparent: true,
        side:        THREE.BackSide,
        blending:    THREE.AdditiveBlending,
        uniforms:    { c: { value: 0.85 }, p: { value: 4.5 }, glowColor: { value: new THREE.Color(0x4aa8ff) } },
        vertexShader: `
          varying vec3 vNormal;
          void main() {
            vNormal = normalize(normalMatrix * normal);
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          uniform float c;
          uniform float p;
          uniform vec3 glowColor;
          varying vec3 vNormal;
          void main() {
            float intensity = pow(c - dot(vNormal, vec3(0.0, 0.0, 1.0)), p);
            gl_FragColor = vec4(glowColor, 1.0) * intensity;
          }
        `,
      });
      const atmo = new THREE.Mesh(
        new THREE.SphereGeometry(GLOBE_RADIUS * 1.16, 64, 64),
        atmoMat,
      );
      scene.add(atmo);

      // ── Starfield background ───────────────────────────────────────────────
      // Procedural fallback: a sphere of random points. Then we ALSO load the
      // night-sky panorama and use it for the back layer when ready.
      const starGeo = new THREE.BufferGeometry();
      const starCount = 1800;
      const starPos = new Float32Array(starCount * 3);
      const starSizes = new Float32Array(starCount);
      for (let i = 0; i < starCount; i++) {
        // Distribute on a large sphere so they always read as background.
        const r = 80 + Math.random() * 40;
        const theta = Math.random() * Math.PI * 2;
        const phi   = Math.acos(2 * Math.random() - 1);
        starPos[i * 3]     = r * Math.sin(phi) * Math.cos(theta);
        starPos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
        starPos[i * 3 + 2] = r * Math.cos(phi);
        starSizes[i] = 0.06 + Math.random() * 0.18;
      }
      starGeo.setAttribute('position', new THREE.BufferAttribute(starPos, 3));
      starGeo.setAttribute('size',     new THREE.BufferAttribute(starSizes, 1));
      const starMat = new THREE.PointsMaterial({
        color:       0xffffff,
        size:        0.18,
        transparent: true,
        opacity:     0.85,
        sizeAttenuation: true,
      });
      const stars = new THREE.Points(starGeo, starMat);
      scene.add(stars);

      // Optional panoramic backdrop sphere (loads async, sits behind everything).
      const skyGeo = new THREE.SphereGeometry(180, 32, 32);
      const skyMat = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        side: THREE.BackSide,
        transparent: true,
        opacity: 0.0,
      });
      const sky = new THREE.Mesh(skyGeo, skyMat);
      scene.add(sky);
      loader.load(TEX.stars, (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace;
        skyMat.map = tex;
        skyMat.opacity = 0.55;
        skyMat.needsUpdate = true;
      });

      // ── Pin dots on globe surface (glowing blue) ───────────────────────────
      localPins.current = PINS.map((p) => {
        const v = latLngToVec3(p.lat, p.lng, GLOBE_RADIUS);
        // Inner bright dot
        const dot = new THREE.Mesh(
          new THREE.SphereGeometry(0.04, 12, 12),
          new THREE.MeshBasicMaterial({ color: 0x60d4ff }),
        );
        dot.position.copy(v);
        globe.add(dot);
        // Soft halo
        const halo = new THREE.Mesh(
          new THREE.SphereGeometry(0.085, 12, 12),
          new THREE.MeshBasicMaterial({
            color: 0x60d4ff, transparent: true, opacity: 0.28, blending: THREE.AdditiveBlending,
          }),
        );
        halo.position.copy(v);
        globe.add(halo);
        return v.clone();
      });

      // ── Lights — sun on the right gives the day/night terminator ───────────
      scene.add(new THREE.AmbientLight(0xffffff, 0.45));
      const sun = new THREE.DirectionalLight(0xfff2dc, 1.6);
      sun.position.set(8, 2, 5);
      scene.add(sun);
      const fill = new THREE.DirectionalLight(0x6ab0ff, 0.35);
      fill.position.set(-6, -2, -3);
      scene.add(fill);

      // ── Resize ─────────────────────────────────────────────────────────────
      ro = new ResizeObserver(() => {
        if (!wrap || !rendRef.current || !camRef.current) return;
        const nW = wrap.offsetWidth;
        const nH = wrap.offsetHeight;
        camRef.current.aspect = nW / nH;
        camRef.current.updateProjectionMatrix();
        rendRef.current.setSize(nW, nH);
      });
      ro.observe(wrap);

      // ── Animation loop ─────────────────────────────────────────────────────
      const quat = new THREE.Quaternion();
      const tmpEuler = new THREE.Euler();
      const tick = () => {
        animId = requestAnimationFrame(tick);
        globe.rotation.y += SPIN_SPEED;
        // Slight reverse drift on the starfield for parallax.
        stars.rotation.y -= SPIN_SPEED * 0.15;

        const cW = wrap.offsetWidth;
        const cH = wrap.offsetHeight;
        tmpEuler.copy(globe.rotation);
        quat.setFromEuler(tmpEuler);

        const next = localPins.current.map((local) => {
          const world = local.clone().applyQuaternion(quat);
          const ndc   = world.clone().project(camera);
          return {
            x: ((ndc.x + 1) / 2) * cW,
            y: ((-ndc.y + 1) / 2) * cH,
            visible: world.z > 0.05,
            depth: world.z,
          };
        });
        setOverlays(next);
        renderer.render(scene, camera);
      };
      tick();
    });

    return () => {
      cancelAnimationFrame(boot);
      cancelAnimationFrame(animId);
      if (ro) ro.disconnect();
      if (rendRef.current) {
        const c = rendRef.current.domElement;
        rendRef.current.dispose();
        if (c && c.parentNode) c.parentNode.removeChild(c);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={wrapRef}
      style={{
        position:   'relative',
        width:      '100%',
        height:     '100%',
        minHeight:  '100vh',
        overflow:   'hidden',
        background: 'radial-gradient(ellipse at 35% 40%, #061226 0%, #020611 60%, #00040b 100%)',
      }}
    >
      {/* ── Subtle horizontal nebula sweep behind the globe ──────────────── */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background:
            'radial-gradient(ellipse 60% 40% at 30% 55%, rgba(74,168,255,0.18), transparent 70%),' +
            'radial-gradient(ellipse 50% 30% at 75% 30%, rgba(244,63,94,0.12), transparent 70%)',
          mixBlendMode: 'screen',
          zIndex: 1,
        }}
      />

      {/* ── Top-left chip ────────────────────────────────────────────────── */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 28,
          zIndex: 30,
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 14px',
            borderRadius: 999,
            background: 'rgba(8,18,38,0.65)',
            border: '1px solid rgba(120,180,255,0.22)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            boxShadow: '0 6px 28px rgba(0,0,0,0.45)',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ec9ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
          <span style={{ color: '#dceaff', fontSize: 12.5, fontWeight: 600, letterSpacing: 0.1 }}>
            Email and Google access
          </span>
        </div>
      </div>

      {/* ── Floating price cards (positioned by Three.js project()) ─────── */}
      {PINS.map((pin, i) => {
        const { x, y, visible, depth } = overlays[i];
        // Cards on the back-half of the globe fade out smoothly.
        const opacity = visible ? Math.min(1, 0.4 + depth * 0.4) : 0;
        // Subtle scale based on depth so closer pins read larger.
        const scale = 0.92 + Math.max(0, depth) * 0.12;
        return (
          <div
            key={pin.city}
            style={{
              position: 'absolute',
              left: x,
              top: y,
              transform: `translate(-50%, calc(-100% - 14px)) scale(${scale})`,
              transformOrigin: 'bottom center',
              pointerEvents: 'none',
              opacity,
              transition: 'opacity 0.25s ease',
              zIndex: 20,
            }}
          >
            {/* White price chip card */}
            <div
              style={{
                background: '#ffffff',
                borderRadius: 12,
                padding: '8px 12px 8px 10px',
                boxShadow: '0 12px 28px -10px rgba(0,0,0,0.55), 0 4px 10px rgba(0,0,0,0.25)',
                whiteSpace: 'nowrap',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 2,
                minWidth: 92,
                border: '1px solid rgba(255,255,255,0.6)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="11" height="14" viewBox="0 0 24 24" fill="#f43f5e" aria-hidden>
                  <path d="M12 2C7.6 2 4 5.6 4 10c0 6.5 8 12 8 12s8-5.5 8-12c0-4.4-3.6-8-8-8zm0 11a3 3 0 110-6 3 3 0 010 6z" />
                </svg>
                <span style={{ color: '#0f172a', fontSize: 12.5, fontWeight: 700, lineHeight: 1.1 }}>
                  {pin.price}
                </span>
                <span
                  style={{
                    background: '#dcfce7',
                    color: '#166534',
                    borderRadius: 4,
                    padding: '1px 5px',
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: 0.3,
                  }}
                >
                  /mo
                </span>
              </div>
              <span style={{ color: '#64748b', fontSize: 10.5, fontWeight: 500, letterSpacing: 0.05 }}>
                {pin.city}
              </span>
            </div>

            {/* Glowing pin dot on the surface */}
            <div
              style={{
                position: 'relative',
                width: 14,
                height: 14,
                margin: '6px auto 0',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  background: 'rgba(96,212,255,0.28)',
                  animation: 'rh-ping 2.4s cubic-bezier(0,0,0.2,1) infinite',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: '#60d4ff',
                  boxShadow: '0 0 12px 3px rgba(96,212,255,0.65), 0 0 4px 1px rgba(255,255,255,0.9) inset',
                }}
              />
            </div>
          </div>
        );
      })}

      {/* ── Bottom: headline + status card ──────────────────────────────── */}
      <div
        style={{
          position: 'absolute',
          bottom: 32,
          left: 32,
          right: 32,
          zIndex: 30,
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
        }}
      >
        <div>
          <h2
            style={{
              color: '#ffffff',
              fontSize: 'clamp(28px, 3.4vw, 44px)',
              fontWeight: 700,
              lineHeight: 1.08,
              margin: 0,
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              textShadow: '0 4px 28px rgba(0,0,0,0.6)',
              letterSpacing: '-0.01em',
            }}
          >
            Step into the live
            <br />
            rental{' '}
            <span style={{ color: '#3aa8ff', fontStyle: 'italic' }}>globe</span>
          </h2>
          <p
            style={{
              color: 'rgba(220,234,255,0.78)',
              fontSize: 14,
              maxWidth: 360,
              margin: '14px 0 0',
              lineHeight: 1.55,
              fontWeight: 400,
            }}
          >
            Explore rooms, flats, and rental prices around the world — all from one
            polished RoomHora account.
          </p>
        </div>

        {/* "Globe view is alive" status card */}
        <div
          style={{
            display: 'inline-flex',
            alignSelf: 'flex-start',
            alignItems: 'center',
            gap: 14,
            padding: '14px 18px',
            borderRadius: 16,
            background: 'rgba(8,18,38,0.78)',
            border: '1px solid rgba(120,180,255,0.18)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            boxShadow: '0 16px 40px rgba(0,0,0,0.55)',
            minWidth: 280,
          }}
        >
          {/* Concentric rotating mark */}
          <div
            style={{
              position: 'relative',
              width: 36,
              height: 36,
              flexShrink: 0,
              borderRadius: '50%',
              background: 'rgba(96,212,255,0.12)',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <span
              aria-hidden
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                border: '1px dashed rgba(96,212,255,0.5)',
                animation: 'rh-spin 9s linear infinite',
              }}
            />
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a3dcff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="12" cy="12" r="10" />
              <line x1="2" y1="12" x2="22" y2="12" />
              <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
            </svg>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
            <span style={{ color: '#eaf3ff', fontSize: 14, fontWeight: 700, letterSpacing: 0.1 }}>
              Globe view is alive
            </span>
            <span style={{ color: 'rgba(190,212,245,0.72)', fontSize: 12, fontWeight: 500 }}>
              Prices, pins, and owners in motion
            </span>
          </div>

          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 9px',
              borderRadius: 999,
              background: 'rgba(34,197,94,0.16)',
              border: '1px solid rgba(34,197,94,0.4)',
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: '#22c55e',
                boxShadow: '0 0 8px 2px rgba(34,197,94,0.6)',
                animation: 'rh-pulse 2s ease-in-out infinite',
              }}
            />
            <span style={{ color: '#86efac', fontSize: 11, fontWeight: 700, letterSpacing: 0.2 }}>
              Live
            </span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes rh-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.4; transform: scale(1.6); }
        }
        @keyframes rh-ping {
          0%   { transform: scale(0.6); opacity: 0.85; }
          80%  { transform: scale(2.2); opacity: 0; }
          100% { transform: scale(2.2); opacity: 0; }
        }
        @keyframes rh-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
